import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../config/supabaseClient';
import { Machine } from '../types';

interface UseMachineDataResult {
  machines: Machine[];
  loading: boolean;
  error: string | null;
}

type CategoryInput = number | number[] | null;

function normalizeIds(input: CategoryInput): number[] | null {
  if (input == null) return null;
  if (Array.isArray(input)) {
    const filtered = input.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    return filtered.length > 0 ? filtered : null;
  }
  return [input];
}

export function useMachineData(
  categoryIds: CategoryInput,
  makeId: number | null
): UseMachineDataResult {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const ids = useMemo(() => normalizeIds(categoryIds), [categoryIds]);
  const idsKey = ids ? ids.slice().sort((a, b) => a - b).join(',') : '';

  useEffect(() => {
    if (!ids || !makeId) {
      setMachines([]);
      return;
    }

    const fetchMachines = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from('machines')
          .select('*')
          .in('category_id', ids)
          .eq('make_id', makeId)
          .order('name');

        if (error) throw error;

        setMachines(data || []);
      } catch (err: any) {
        console.error("Error fetching machines:", err);
        setError(err.message || "Failed to load machines");
        setMachines([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMachines();
    // idsKey makes the effect stable across new array identities with the same contents
  }, [idsKey, makeId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { machines, loading, error };
}
